import { useId } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { ScoreSnapshot } from '../types';
import { toGGScore, GG_BASELINE, getRiskColor, getStatusLabel } from '../utils/score';

interface ScoreTrendChartProps {
  snapshots: ScoreSnapshot[];
  height?: number;
}

function formatWeek(weekStart: string): string {
  const d = new Date(weekStart + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function scoreToHex(color: 'green' | 'yellow' | 'red'): string {
  switch (color) {
    case 'green': return '#10b981';
    case 'yellow': return '#f59e0b';
    case 'red': return '#ef4444';
  }
}

export default function ScoreTrendChart({ snapshots, height = 240 }: ScoreTrendChartProps) {
  const gradientId = useId().replace(/:/g, '');

  if (!snapshots.length) {
    return (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        No score history yet. Scores appear after weekly scoring runs.
      </div>
    );
  }

  const data = snapshots.map(s => ({
    week: formatWeek(s.week_start),
    score: toGGScore(Number(s.score_value)),
  }));

  const latestScore = data[data.length - 1]?.score ?? GG_BASELINE;
  const lineColor = scoreToHex(getRiskColor(latestScore));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={lineColor} stopOpacity={0.2} />
            <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="week"
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={{ stroke: '#e2e8f0' }}
        />
        <YAxis
          domain={[300, 900]}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          ticks={[300, 450, 600, 750, 900]}
        />
        <ReferenceLine
          y={GG_BASELINE}
          stroke="#94a3b8"
          strokeDasharray="4 4"
          label={{ value: 'Baseline', position: 'right', fontSize: 10, fill: '#94a3b8' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1e293b',
            border: 'none',
            borderRadius: '8px',
            color: '#f8fafc',
            fontSize: '12px',
          }}
          formatter={(value) => {
            const v = Number(value ?? 0);
            return [`${v} — ${getStatusLabel(v)}`, 'GuardGrade Score'];
          }}
          labelFormatter={(label) => `Week of ${label}`}
        />
        <Area
          type="monotone"
          dataKey="score"
          stroke={lineColor}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={{ r: 4, fill: lineColor, strokeWidth: 2, stroke: '#fff' }}
          activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
